import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Volume2, VolumeX, Megaphone, Bell, Vibrate, X } from "lucide-react";
import { playChime } from "@/lib/chime";

export function NotificationPrefsCard() {
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    try { return window.localStorage.getItem("notif:chime") !== "off"; } catch { return true; }
  });
  const [style, setStyle] = useState(() => {
    try { return window.localStorage.getItem("notif:chime-style") || "ping"; } catch { return "ping"; }
  });

  const persistChime = (v) => {
    setChimeEnabled(v);
    try { window.localStorage.setItem("notif:chime", v ? "on" : "off"); } catch { /* ignore */ }
    window.dispatchEvent(new StorageEvent("storage", { key: "notif:chime", newValue: v ? "on" : "off" }));
    toast.success(v ? "Chime enabled" : "Chime muted");
  };

  const persistStyle = (v) => {
    setStyle(v);
    try { window.localStorage.setItem("notif:chime-style", v); } catch { /* ignore */ }
    window.dispatchEvent(new StorageEvent("storage", { key: "notif:chime-style", newValue: v }));
    playChime(v);
  };

  return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="notification-prefs-card">
      <div className="font-heading font-medium mb-1">Notification Preferences</div>
      <div className="text-sm text-slate-500 mb-4">Play a soft chime when a new notification arrives while you're working. Saved on this device only.</div>
      <div className="flex flex-wrap items-end gap-3">
        <Button
          variant={chimeEnabled ? "default" : "outline"}
          size="sm"
          className={chimeEnabled ? "bg-brand hover:bg-brand-hover" : ""}
          onClick={() => persistChime(!chimeEnabled)}
          data-testid="chime-toggle"
        >
          {chimeEnabled ? <Volume2 className="w-4 h-4 mr-2" /> : <VolumeX className="w-4 h-4 mr-2" />}
          {chimeEnabled ? "Chime is ON" : "Chime is OFF"}
        </Button>
        <div className="min-w-[200px]">
          <Label className="text-[11px] uppercase text-slate-500">Chime style</Label>
          <Select value={style} onValueChange={persistStyle} disabled={!chimeEnabled}>
            <SelectTrigger data-testid="chime-style"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ping"><span className="inline-flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> Soft ping</span></SelectItem>
              <SelectItem value="bell"><span className="inline-flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> Gentle bell</span></SelectItem>
              <SelectItem value="vibrate"><span className="inline-flex items-center gap-2"><Vibrate className="w-3.5 h-3.5" /> Mute · vibrate only</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => playChime(style)} disabled={!chimeEnabled} data-testid="chime-test">Test chime</Button>
      </div>
      <div className="mt-3 text-xs text-slate-500">
        Mute · vibrate is silent — on supported devices (mostly mobile browsers) it triggers a short haptic pulse instead.
      </div>
    </Card>
  );
}

export function BroadcastBannerCard() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/settings/broadcast-banners");
      const list = (data?.banners || []).map((b) => ({
        id: b.id,
        enabled: !!b.enabled,
        message: b.message || "",
        kind: b.kind || "info",
        start_at: b.start_at ? b.start_at.slice(0, 16) : "",
        end_at: b.end_at ? b.end_at.slice(0, 16) : "",
        active: !!b.active,
      }));
      setBanners(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (idx, patch) => setBanners((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  const addSlot = () => setBanners((prev) => [...prev, { id: null, enabled: false, message: "", kind: "info", start_at: "", end_at: "", active: false }]);
  const removeSlot = (idx) => setBanners((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    // Validate every enabled slot
    for (const b of banners) {
      if (b.enabled && !b.message.trim()) {
        toast.error("Every enabled announcement needs a message.");
        return;
      }
      if (b.start_at && b.end_at && b.end_at <= b.start_at) {
        toast.error("End time must be after start time for every slot.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        banners: banners.map((b) => ({
          id: b.id || null,
          enabled: b.enabled,
          message: b.message,
          kind: b.kind,
          start_at: b.start_at ? new Date(b.start_at).toISOString() : null,
          end_at: b.end_at ? new Date(b.end_at).toISOString() : null,
        })),
      };
      const { data } = await api.put("/api/settings/broadcast-banners", payload);
      const list = (data || []).map((b) => ({
        id: b.id, enabled: !!b.enabled, message: b.message || "", kind: b.kind || "info",
        start_at: b.start_at ? b.start_at.slice(0, 16) : "",
        end_at: b.end_at ? b.end_at.slice(0, 16) : "",
        active: !!b.active,
      }));
      setBanners(list);
      toast.success("Broadcast queue saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="broadcast-banner-card">
      <div className="font-heading font-medium mb-1 flex items-center gap-2">
        <Megaphone className="w-4 h-4 text-brand" />
        Broadcast Banner Queue (News / Announcements)
      </div>
      <div className="text-sm text-slate-500 mb-4">
        Queue multiple announcements — the banner will rotate through active ones every 8 seconds. Optional start / end windows let holiday closures publish and expire automatically.
      </div>
      <div className="space-y-4">
        {banners.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-4 text-center">
            No announcements yet. Click "Add announcement" below.
          </div>
        )}
        {banners.map((b, idx) => (
          <div key={b.id || `new-${idx}`} className="rounded-md border border-slate-200 p-4" data-testid={`broadcast-slot-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-slate-700">
                Slot #{idx + 1}
                {b.active && <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] px-1.5 py-0.5">LIVE NOW</span>}
                {b.enabled && !b.active && <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[10px] px-1.5 py-0.5">SCHEDULED / EXPIRED</span>}
              </div>
              <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => removeSlot(idx)} data-testid={`broadcast-remove-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs">Message</Label>
                <Textarea
                  rows={2}
                  value={b.message}
                  onChange={(e) => update(idx, { message: e.target.value })}
                  placeholder="e.g. Office closed 26 Jan · Republic Day."
                  data-testid={`broadcast-message-${idx}`}
                />
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Show from (optional)</Label>
                    <Input type="datetime-local" value={b.start_at} onChange={(e) => update(idx, { start_at: e.target.value })} data-testid={`broadcast-start-${idx}`} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Hide after (optional)</Label>
                    <Input type="datetime-local" value={b.end_at} onChange={(e) => update(idx, { end_at: e.target.value })} data-testid={`broadcast-end-${idx}`} />
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Style</Label>
                  <Select value={b.kind} onValueChange={(v) => update(idx, { kind: v })}>
                    <SelectTrigger data-testid={`broadcast-kind-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info (slate)</SelectItem>
                      <SelectItem value="warning">Alert (orange)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={b.enabled}
                    onChange={(e) => update(idx, { enabled: e.target.checked })}
                    data-testid={`broadcast-enabled-${idx}`}
                  />
                  Publish this slot
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={addSlot} data-testid="broadcast-add-slot">
          + Add announcement
        </Button>
        <Button className="bg-brand hover:bg-brand-hover" onClick={save} disabled={saving || loading} data-testid="broadcast-save">
          {saving ? "Saving…" : "Save queue"}
        </Button>
      </div>
    </Card>
  );
}
